import { useTranslation } from 'react-i18next';
import { AuthAction } from '@/types/gql.generated';
import { AuthHeader } from '../AuthHeader';
import type { AuthHeaderRenderProp } from '../types';

type Props = {
  authAction: AuthAction | null;
  /** Passed through to `renderHeader` so consumers can show where the code was sent. */
  email?: string;
  /** The post-verification name prompt, which has no AuthAction of its own. */
  isNameStep?: boolean;
  renderHeader?: AuthHeaderRenderProp;
};

export const AuthHeaderRenderer = ({
  authAction,
  email,
  isNameStep,
  renderHeader,
}: Props) => {
  const { t } = useTranslation('auth');

  // Handled before delegating: the name step is not an AuthAction, so a consumer
  // supplying `renderHeader` has no way to know about it and would fall through to
  // its own catch-all - which on a login-framed screen reads as "log in" copy
  // above a name field, well after the user has already logged in.
  if (isNameStep) {
    return (
      <AuthHeader title={t('name_prompt_title')}>
        {t('name_prompt_byline')}
      </AuthHeader>
    );
  }

  if (renderHeader) {
    return renderHeader({
      authAction,
      email,
    });
  }

  let title = '';
  let byline = '';

  if (authAction === null) {
    title = t('sign_in_or_create');
  } else if (authAction === AuthAction.Register) {
    title = t('create_free_account');
  } else if (authAction === AuthAction.LoginPassword) {
    title = t('welcome_back');
    byline = t('sign_into_account');
  } else if (authAction === AuthAction.LoginGoogle) {
    title = t('welcome_back');
    byline = t('sign_in_with_google');
  } else if (authAction === AuthAction.LoginApple) {
    title = t('welcome_back');
    byline = t('sign_in_with_apple');
  } else if (authAction === AuthAction.LoginEmailCode) {
    title = t('check_your_email');
  }

  if (title) {
    return <AuthHeader title={title}>{byline}</AuthHeader>;
  }

  return null;
};
